import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isPlatformChromeTenant } from '../../app/platformChromeTenant.js';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import { useCustomerFeatureFlags } from '../flags/useCustomerFeatureFlags.js';
import { isCustomerBrowseEnabled, isCustomerScanEnabled } from '../../config/customerFeatureFlags.js';
import { useCustomerSurfaceEntitlement } from '../entitlements/customerEntitlementGuards.js';
import { isGloballySignedInChrome } from '../hub/customerHubPaths.js';
import { markTenantCustomerDiscoveryUnlistable } from '../shop/customerDiscoveryListability.js';
import { giftVisibilityForOverflow } from './giftOverflowPolicy.js';
import { useGiftSurfaceVisibility } from './useGiftSurfaceVisibility.js';
import {
  buildCustomerNavComposition,
  type NavComposition,
} from './customerNavComposition.js';
import type { GiftSurfaceVisibility } from './giftSurfaceVisibility.js';

export function useCustomerNavModel(): {
  readonly composition: NavComposition;
  readonly giftReady: boolean;
  readonly giftForOverflow: GiftSurfaceVisibility;
  readonly showFindOrder: boolean;
  readonly showKupony: boolean;
  readonly showFavorites: boolean;
  readonly showShops: boolean;
  readonly showBrowse: boolean;
  readonly showScan: boolean;
  readonly showLoyalty: boolean;
} {
  const { t } = useTranslation('customer');
  const tenantCode = useTenantCode();
  const { session } = useCustomerSession();
  const { flags } = useCustomerFeatureFlags();
  const {
    isBlockEntitled,
    isLoading: entitlementLoading,
    snapshot: entitlementSnapshot,
  } = useCustomerSurfaceEntitlement();
  const gift = useGiftSurfaceVisibility();

  const platformChrome = isPlatformChromeTenant(tenantCode);

  // Settled deny of surface_customer|product_vending → mark unlistable for directory.
  useEffect(() => {
    if (entitlementLoading || entitlementSnapshot === null || platformChrome) {
      return;
    }
    const blocks = entitlementSnapshot.blocks;
    const listable =
      blocks !== undefined &&
      blocks !== null &&
      blocks.surface_customer === true &&
      blocks.product_vending === true;
    if (!listable) {
      markTenantCustomerDiscoveryUnlistable(tenantCode);
    }
  }, [platformChrome, entitlementLoading, entitlementSnapshot, tenantCode]);

  const showFindOrder = isBlockEntitled('order_pickup_infrastructure');
  const showKupony = flags.customerKuponyEnabled === true;
  const showFavorites = flags.customerFavoritesNavEnabled === true;
  const showShops = flags.customerShopsNavEnabled === true;
  const showLoyalty = flags.customerLoyaltyNavEnabled === true;
  const showBrowse = isCustomerBrowseEnabled(flags);
  const showScan = isCustomerScanEnabled(flags);

  const isAuthenticated = isGloballySignedInChrome({ session });

  const giftForOverflow = useMemo(
    () => giftVisibilityForOverflow(gift),
    [gift],
  );

  const composition = useMemo(
    () =>
      buildCustomerNavComposition({
        tenantCode,
        t,
        gift: giftForOverflow,
        isAuthenticated,
        showFindOrder,
        showKupony,
        showShops,
      }),
    [tenantCode, giftForOverflow, isAuthenticated, showFindOrder, showKupony, showShops, t],
  );

  return {
    composition,
    giftReady: gift.masterVisible || !isBlockEntitled('donation'),
    giftForOverflow,
    showFindOrder,
    showKupony,
    showFavorites,
    showShops,
    showLoyalty,
    showBrowse,
    showScan,
  };
}
